import { useState, useEffect } from "react";
import { Button, Spinner } from "@swivl-mono/ui";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { API } from "@swivl-mono/api-helper";
import { Member } from "../../../Models/Member/Member.Current";
import { loadGoogleSheetNames } from "./Onboarding.GoogleSheetImport";
import { extractSheetId, getColumnLetter, parseEmails } from "./shared/onboarding.utils";
import { styles } from "./shared/onboarding.styles";
import AppsModel from "../../../Models/Apps/Apps.Model";
import { UnitInterface } from "@swivl-mono/types";

const FEATURES_TO_ANALYZE = [
    '24 Hour Access',
    'After Hours Access',
    'Appointments',
    'Climate Controlled Units',
    'Conference Room',
    'Deliveries Accepted',
    'Drive-Up Units',
    'Dollies',
    'Dumpster',
    'Electricity In Unit',
    'Elevators',
    'Heated Units',
    'Moving Truck',
    'Notary',
    'Office Space',
    'Padlock Codes',
    'Vehicle Storage',
    'Small Vehicle',
    'Indoor Vehicle',
    'Outdoor Vehicle',
    'Covered Vehicle',
    'RV',
    'Covered RV',
    'Enclosed RV',
    'Trailer',
    'Business Storage',
    'Lockers',
    'Wine',
    'Boat',
    'Video Surveillance'
];

const extractFMSText = (units: UnitInterface[], fmsType: string): string => {
    return units.map(unit => {
        try {
            const raw = typeof unit.raw === 'string' ? JSON.parse(unit.raw) : (unit.raw || {});

            switch (fmsType) {
                case 'StorEdge':
                    return [
                        raw.description,
                        raw.unit_type?.name,
                        raw.access_type,
                        raw.unit_amenities?.map((a: any) => a.name)?.join(' ')
                    ].filter(Boolean).join(' ');

                case 'SiteLink':
                    return [
                        raw.sUnitDesc,
                        raw.sUnitName,
                        raw.sTypeName,
                        raw.bClimate === 'true' ? 'Climate Controlled' : '',
                        raw.bInside === 'true' ? 'Indoor' : 'Outdoor',
                        raw.bPower === 'true' ? 'Power' : '',
                        raw.bAlarm === 'true' ? 'Alarm' : ''
                    ].filter(Boolean).join(' ');

                case 'Cubby':
                    return [
                        raw.name,
                        raw.pricingGroup?.name,
                        raw.pricingGroup?.features?.join(' '),
                        raw.floorName ? `Floor ${raw.floorName}` : ''
                    ].filter(Boolean).join(' ');

                case 'Tenant':
                    return [
                        raw.Category?.name,
                        raw.Category?.description,
                        raw.Amenities?.['Space Features']?.map((a: any) => `${a.name}: ${a.value}`)?.join(' '),
                        raw.Amenities?.['Access']?.map((a: any) => `${a.name}: ${a.value}`)?.join(' '),
                        raw.floor ? `Floor ${raw.floor}` : ''
                    ].filter(Boolean).join(' ');

                case 'SSMCloud':
                    return [
                        raw.CategoryName,
                        raw.UnitTypeDescription,
                        raw.UnitTypeCode,
                        raw.FloorNumber ? `Floor ${raw.FloorNumber}` : ''
                    ].filter(Boolean).join(' ');

                default:
                    return [
                        raw.description,
                        raw.name,
                        raw.type,
                        raw.unitType
                    ].filter(Boolean).join(' ');
            }
        } catch (error) {
            console.warn(`Error extracting text from unit ${unit.id}:`, error);
            return unit.name || unit.number || '';
        }
    }).join(' ').trim();
};

const FEATURE_PATTERNS: Record<string, RegExp[]> = {
    '24 Hour Access': [/24\s*hour/i, /24\/7/i, /round\s*the\s*clock/i, /always\s*accessible/i, /anytime\s*access/i],
    'After Hours Access': [/after\s*hours/i, /extended\s*access/i, /late\s*access/i, /extended\s*hours/i],
    'Appointments': [/appointment/i, /by\s*appointment/i, /scheduled\s*access/i, /call\s*ahead/i],
    'Climate Controlled Units': [/climate\s*control/i, /temperature\s*control/i, /heated/i, /air\s*condition/i, /hvac/i, /temp\s*control/i],
    'Conference Room': [/conference\s*room/i, /meeting\s*room/i, /business\s*center/i, /office\s*space/i],
    'Deliveries Accepted': [/deliver/i, /package\s*accept/i, /mail\s*accept/i, /ups\s*fedex/i, /shipping\s*receive/i],
    'Drive-Up Units': [/drive\s*up/i, /drive-up/i, /drive\s*through/i, /vehicle\s*access/i, /car\s*access/i, /loading\s*dock/i],
    'Dollies': [/dolly/i, /dollies/i, /hand\s*truck/i, /moving\s*cart/i, /equipment\s*rental/i],
    'Dumpster': [/dumpster/i, /trash\s*disposal/i, /garbage\s*disposal/i, /waste\s*disposal/i],
    'Electricity In Unit': [/electric/i, /power/i, /outlet/i, /plug/i, /electrical/i],
    'Elevators': [/elevator/i, /lift/i, /multiple\s*floor/i, /upper\s*level/i],
    'Heated Units': [/heated/i, /heat/i, /warm\s*storage/i, /temperature\s*controlled/i],
    'Moving Truck': [/moving\s*truck/i, /rental\s*truck/i, /u\s*haul/i, /truck\s*rental/i],
    'Notary': [/notary/i, /notarial/i, /document\s*service/i],
    'Office Space': [/office\s*space/i, /business\s*office/i, /workspace/i, /meeting\s*space/i],
    'Padlock Codes': [/padlock\s*code/i, /combination\s*lock/i, /digital\s*lock/i, /keypad/i, /access\s*code/i],
    'Vehicle Storage': [/vehicle\s*storage/i, /car\s*storage/i, /auto\s*storage/i, /automotive/i],
    'Small Vehicle': [/small\s*vehicle/i, /motorcycle/i, /atv/i, /golf\s*cart/i, /scooter/i],
    'Indoor Vehicle': [/indoor\s*vehicle/i, /enclosed\s*vehicle/i, /garage\s*storage/i, /covered\s*car/i],
    'Outdoor Vehicle': [/outdoor\s*vehicle/i, /open\s*parking/i, /uncovered\s*vehicle/i, /parking\s*lot/i],
    'Covered Vehicle': [/covered\s*vehicle/i, /carport/i, /covered\s*parking/i, /canopy\s*parking/i],
    'RV': [/rv\s*storage/i, /recreational\s*vehicle/i, /motor\s*home/i, /camper/i, /travel\s*trailer/i],
    'Covered RV': [/covered\s*rv/i, /canopy\s*rv/i, /sheltered\s*rv/i, /carport\s*rv/i],
    'Enclosed RV': [/enclosed\s*rv/i, /indoor\s*rv/i, /garage\s*rv/i, /building\s*rv/i],
    'Trailer': [/trailer/i, /utility\s*trailer/i, /cargo\s*trailer/i, /boat\s*trailer/i],
    'Business Storage': [/business\s*storage/i, /commercial\s*storage/i, /office\s*storage/i, /document\s*storage/i],
    'Lockers': [/locker/i, /small\s*storage/i, /personal\s*locker/i, /mini\s*storage/i],
    'Wine': [/wine\s*storage/i, /wine\s*cellar/i, /temperature\s*controlled.*wine/i, /beverage\s*storage/i],
    'Boat': [/boat\s*storage/i, /marine\s*storage/i, /watercraft/i, /vessel\s*storage/i],
    'Video Surveillance': [/video\s*surveillance/i, /security\s*camera/i, /cctv/i, /surveillance\s*system/i, /security\s*monitoring/i, /video\s*monitoring/i]
};

const detectFeature = async (orgId: number, feature: string, text: string): Promise<boolean> => {
    const patterns = FEATURE_PATTERNS[feature] || [];

    for (const pattern of patterns) {
        if (pattern.test(text)) {
            const prompt = `Analyze this text to confirm if "${feature}" is actually AVAILABLE/OFFERED:

"${text}"

The text contains keywords for "${feature}", but confirm:
- Is the feature actually available? (not just mentioned)
- Is it offered as a service? (not disabled/unavailable)
- Look for context like "available", "offered", vs "not available", "false", "disabled"

Return only "true" if the feature is confirmed available, "false" otherwise.`;

            try {
                const response = await API.AI.getPromptResponse(orgId, 'gpt-4o', prompt, 10);
                return response.toLowerCase().includes('true');
            } catch (error) {
                console.warn(`LLM confirmation failed for ${feature}, using regex result`);
                return true;
            }
        }
    }

    return false;
};


export function FMSScrapeView() {
    const [sheetUrl, setSheetUrl] = useState("");
    const [emails, setEmails] = useState("");
    const [isProcessing, setIsProcessing] = useState(false);
    const [availableSheets, setAvailableSheets] = useState<string[]>([]);
    const [selectedSheet, setSelectedSheet] = useState<string>("");
    const [isLoadingSheets, setIsLoadingSheets] = useState(false);
    const [hasMandrill, setHasMandrill] = useState(false);
    const [isCheckingMandrill, setIsCheckingMandrill] = useState(true);

    useEffect(() => {
        if (sheetUrl.trim()) {
            try {
                const sheetId = extractSheetId(sheetUrl);
                loadGoogleSheetNames(sheetId, setIsLoadingSheets, setAvailableSheets, setSelectedSheet);
            } catch (error) {
                setAvailableSheets([]);
                setSelectedSheet("");
            }
        } else {
            setAvailableSheets([]);
            setSelectedSheet("");
        }
    }, [sheetUrl]);

    useEffect(() => {
        const checkMandrill = async () => {
            try {
                await AppsModel.load(false);
                const mandrillApp = AppsModel.state.apps?.find(app => app.type === 'mandrill');
                setHasMandrill(!!mandrillApp?.token?.mandrill_api_key);
            } catch (error) {
                console.warn("Failed to check Mandrill configuration:", error);
                setHasMandrill(false);
            } finally {
                setIsCheckingMandrill(false);
            }
        };
        checkMandrill();
    }, []);

    const sendEmailNotification = async (result: any, emailArray: string[], sheetId: string) => {
        try {
            await AppsModel.load(false);
            const mandrillApp = AppsModel.state.apps?.find(app => app.type === 'mandrill');

            const fromEmail = mandrillApp?.data?.mandrill_from_email || 'info@tryswivl.com';
            const fromName = mandrillApp?.data?.mandrill_from_name || 'swivl';
            const orgName = Member.org?.name || 'Your Organization';
            const sheetUrl = `https://docs.google.com/spreadsheets/d/${sheetId}/edit`;

            const subject = `FMS Scraping Complete - ${orgName}`;
            const body = `Hi there!

The FMS scraping for ${orgName} has completed successfully.

Summary:
• Processed - ${result.processed} locations
• Features detected - ${result.featuresDetected} total features ${result.detectedFeatures && result.detectedFeatures.length > 0 ? `(${result.detectedFeatures.join(', ')})` : ''}
• Cells updated - ${result.cellsUpdated}
• Failed - ${result.failed} locations
• No unit data - ${result.noUnitData} locations

${result.failedLocations && result.failedLocations.length > 0 ? `❌ Failed locations:\n${result.failedLocations.slice(0, 10).map(loc => `• ${loc}`).join('\n')}${result.failedLocations.length > 10 ? `\n• ... and ${result.failedLocations.length - 10} more` : ''}\n\n` : ''}${result.noUnitDataLocations && result.noUnitDataLocations.length > 0 ? `⚠️ Locations with no unit data:\n${result.noUnitDataLocations.slice(0, 10).map(loc => `• ${loc}`).join('\n')}${result.noUnitDataLocations.length > 10 ? `\n• ... and ${result.noUnitDataLocations.length - 10} more` : ''}\n\n` : ''}${result.errors && result.errors.length > 0 ? `🔍 Error details:\n${result.errors.slice(0, 5).map(e => `• ${e}`).join('\n')}${result.errors.length > 5 ? `\n• ... and ${result.errors.length - 5} more` : ''}\n\n` : ''}📊 View your updated Google Sheet: ${sheetUrl}

The sheet has been updated with detected features marked in 🟢 green 🟢 You can review and modify any suggestions as needed.

Questions? Feel free to reach out to Jenna.`;

            if (!mandrillApp?.token?.mandrill_api_key) {
                throw new Error("Mandrill API key not found");
            }

            const apiKey = mandrillApp.token.mandrill_api_key;

            for (const email of emailArray) {
                const message = {
                    from_email: fromEmail,
                    from_name: fromName,
                    to: [{ email: email.trim(), type: "to" }],
                    subject: subject,
                    text: body
                };

                const response = await fetch('https://mandrillapp.com/api/1.0/messages/send.json', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        key: apiKey,
                        message: message
                    })
                });

                if (!response.ok) {
                    throw new Error(`Mandrill API error: ${response.status} ${response.statusText}`);
                }

                const result = await response.json();

                if (result[0]?.status === 'rejected') {
                    throw new Error(`Email rejected: ${result[0]?.reject_reason || 'Unknown reason'}`);
                }
            }

        } catch (error) {
            console.warn("Failed to prepare email notification:", error);
        }
    };

    const handleScrape = async () => {
        if (!sheetUrl.trim() || !emails.trim()) {
            AlertsModel.errorToast("Please enter both Sheet URL and emails");
            return;
        }

        if (availableSheets.length > 1 && !selectedSheet) {
            AlertsModel.errorToast("Please select a sheet");
            return;
        }

        setIsProcessing(true);

        const startTime = Date.now();
        const errors: string[] = [];
        const processedLocations: string[] = [];
        const failedLocations: string[] = [];
        const noUnitDataLocations: string[] = [];
        const detectedFeaturesSet = new Set<string>();
        let cellsUpdated = 0;

        try {
            const sheetId = extractSheetId(sheetUrl);
            const emailArray = parseEmails(emails);
            const orgId = Member.orgId;

            if (!orgId) {
                throw new Error("No org selected. Please login to a valid account.");
            }

            const sheetData = await API.Google.readGoogleSheet(sheetId, selectedSheet ? `${selectedSheet}!A:ZZ` : 'A:ZZ');

            if (!sheetData || !sheetData.records || sheetData.records.length === 0) {
                throw new Error('No data found in Google Sheet');
            }

            const locations = sheetData.records.filter(record => record['Location ID']);

            if (locations.length === 0) {
                throw new Error('No locations with Location ID found in sheet');
            }

            const batchSize = 5;
            for (let i = 0; i < locations.length; i += batchSize) {
                const batch = locations.slice(i, i + batchSize);

                for (const record of batch) {
                    const locationId = parseInt(record['Location ID']);
                    const locationName = record['Location Name'] || `Location ${locationId}`;

                    if (isNaN(locationId)) {
                        console.warn(`Invalid Location ID: ${record['Location ID']}`);
                        continue;
                    }

                    try {
                        const units = await API.Units.getUnitsByLocationId(orgId, locationId, {});

                        if (!units || units.length === 0) {
                            noUnitDataLocations.push(`${locationName} (ID: ${locationId})`);
                            continue;
                        }

                        const unitsByFMS = units.reduce((acc, unit) => {
                            const fmsType = unit.fmsType || 'Unknown';
                            if (!acc[fmsType]) acc[fmsType] = [];
                            acc[fmsType].push(unit);
                            return acc;
                        }, {} as Record<string, UnitInterface[]>);

                        let allText = '';
                        for (const [fmsType, fmsUnits] of Object.entries(unitsByFMS)) {
                            const extractedText = extractFMSText(fmsUnits, fmsType);
                            allText += ' ' + extractedText;
                        }

                        allText = allText.trim();

                        if (!allText) {
                            noUnitDataLocations.push(`${locationName} (ID: ${locationId})`);
                            continue;
                        }

                        const detectedFeatures: string[] = [];
                        for (const feature of FEATURES_TO_ANALYZE) {
                            const colIndex = sheetData.headers.findIndex(h => h === feature);
                            if (colIndex >= 0) {
                                const existingValue = record[feature];
                                if (!existingValue || existingValue.trim() === '') {
                                    const isDetected = await detectFeature(orgId, feature, allText);
                                    if (isDetected) {
                                        detectedFeatures.push(feature);
                                        detectedFeaturesSet.add(feature);
                                    }
                                }
                            }

                            await new Promise(resolve => setTimeout(resolve, 100));
                        }

                        if (detectedFeatures.length > 0) {
                            const rowIndex = sheetData.records.findIndex(r => r['Location ID'] === record['Location ID']) + 2; // +2 for header row and 1-based indexing

                            for (const featureName of detectedFeatures) {
                                const colIndex = sheetData.headers.findIndex(h => h === featureName);
                                if (colIndex >= 0) {
                                    const colLetter = getColumnLetter(colIndex);
                                    const range = selectedSheet ? `${selectedSheet}!${colLetter}${rowIndex}` : `${colLetter}${rowIndex}`;

                                    await API.Google.updateGoogleSheet(sheetId, range, [['TRUE']], {
                                        backgroundColor: '#90EE90',
                                        sheetName: selectedSheet
                                    });
                                    cellsUpdated++;
                                }
                            }
                        }

                        processedLocations.push(`${locationName} (ID: ${locationId})`);

                    } catch (error) {
                        failedLocations.push(`${locationName} (ID: ${locationId})`);
                        const errorMsg = `Failed to process location ${locationName}: ${(error as Error)?.message || 'Unknown error'}`;
                        errors.push(errorMsg);
                        console.error(errorMsg, error);
                    }
                }

                if (i + batchSize < locations.length) {
                    await new Promise(resolve => setTimeout(resolve, 2000));
                }
            }

            const processingTime = Math.round((Date.now() - startTime) / 1000);
            const summary = `Processed ${processedLocations.length} locations in ${processingTime}s. Found ${detectedFeaturesSet.size} features, updated ${cellsUpdated} cells.`;

            const result = {
                success: true,
                processed: processedLocations.length,
                failed: failedLocations.length,
                noUnitData: noUnitDataLocations.length,
                featuresDetected: detectedFeaturesSet.size,
                cellsUpdated,
                detectedFeatures: Array.from(detectedFeaturesSet).sort(),
                failedLocations,
                noUnitDataLocations,
                errors,
                summary
            };

            if (emailArray.length > 0) {
                await sendEmailNotification(result, emailArray, sheetId);
            }

            AlertsModel.successToast(
                `Processed ${processedLocations.length} locations! ${cellsUpdated} cells updated with ${detectedFeaturesSet.size} features detected.`
            );

        } catch (error) {
            const errorMsg = `FMS feature scraping failed: ${(error as Error)?.message || 'Unknown error'}`;
            console.error(errorMsg, error);

            const result = {
                success: false,
                processed: processedLocations.length,
                failed: failedLocations.length + 1,
                noUnitData: noUnitDataLocations.length,
                featuresDetected: detectedFeaturesSet.size,
                cellsUpdated,
                detectedFeatures: Array.from(detectedFeaturesSet).sort(),
                failedLocations,
                noUnitDataLocations,
                errors: [...errors, errorMsg],
                summary: `Scraping failed after processing ${processedLocations.length} locations: ${errorMsg}`
            };

            if (emails.trim()) {
                const emailArray = parseEmails(emails);
                await sendEmailNotification(result, emailArray, extractSheetId(sheetUrl));
            }

            AlertsModel.errorToast(`Failed: ${errorMsg}`);
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div style={styles.container}>
            <h2 style={{ fontSize: '18px', fontWeight: 600, marginBottom: '10px' }}>
                3️⃣ Scrape FMS Data
            </h2>
            <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                Use LLMs to determine which FMS features a location has. Only blank fields will be analyzed, and updated to TRUE with a green background if detected.
            </p>

            <div style={styles.requirementsBox}>
                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', fontWeight: 600, color: '#333' }}>Requirements:</h4>
                <ol style={{ margin: 0, paddingLeft: '20px', fontSize: '14px', color: '#666' }}>
                    <li>
                        {isCheckingMandrill ? (
                            <>⏳ Checking email configuration...</>
                        ) : hasMandrill ? (
                            <>✅ Mandrill configured for email notifications</>
                        ) : (
                            <>❌ <a href="/settings/apps/mandrill" target="_blank" rel="noopener noreferrer" style={{ color: '#0066cc' }}>Mandrill </a> needs to be configured</>
                        )}
                    </li>
                    <li>📝 Location IDs exist in your Google Sheet</li>
                </ol>
            </div>

            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    value={sheetUrl}
                    onChange={(e) => setSheetUrl(e.target.value)}
                    placeholder="Google Sheet URL"
                    disabled={isProcessing}
                    style={styles.input}
                />
            </div>

            {availableSheets.length > 1 && (
                <div style={{ marginBottom: '20px' }}>
                    <select
                        value={selectedSheet}
                        onChange={(e) => setSelectedSheet(e.target.value)}
                        disabled={isProcessing || isLoadingSheets}
                        style={styles.input}
                    >
                        <option value="">Select a sheet...</option>
                        {availableSheets.map(sheet => (
                            <option key={sheet} value={sheet}>{sheet}</option>
                        ))}
                    </select>
                </div>
            )}

            <div style={{ marginBottom: '20px' }}>
                <input
                    type="text"
                    value={emails}
                    onChange={(e) => setEmails(e.target.value)}
                    placeholder="Email addresses (comma-separated)"
                    disabled={isProcessing}
                    style={styles.input}
                />
            </div>

            <Button
                onClick={handleScrape}
                disabled={isProcessing || !sheetUrl || !emails || isCheckingMandrill || !hasMandrill}
            >
                {isProcessing ? (
                    <>
                        <Spinner />
                        {' Processing...'}
                    </>
                ) : (
                    "Start Scraping"
                )}
            </Button>

            {isProcessing && (
                <p style={{ marginTop: '15px', fontSize: '13px', color: '#666' }}>
                    ⚠️ This process can take up to 30 minutes. You'll receive an email when complete. Feel free to navigate away from this page.
                </p>
            )}
        </div>
    );
}
